// Imports nothing from the shell: modules/index.ts and player-ai/scope.ts import this file.
import { LAMA_ASSESS_SEGMENT } from "@playbookai/types";
import type {
  AppLauncherEntry,
  ModuleId,
  ModuleManifest,
  ModuleTint,
  Tab,
  TabAccent,
} from "@playbookai/ui";
import { DEFAULT_KNOWLEDGE_INSTANCE_ID } from "@playbookai/knowledge-studio-feature";
import { PRODUCT_NAME } from "@playbookai/types";
import { A3Sheet, MaturityWeb, ProcessChain, TierBoard } from "@playbookai/ui";
import { BookOpen, Compass, type LucideIcon } from "lucide-react";

/** An item id as the shell's routes carry it. */
export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Per-module presentation metadata — short tag, identity colour, and the
 * one-line blurb the launcher displays. Kept in a single registry so adding
 * a module touches one row here in addition to one row in `ALL_MODULES`.
 *
 * This is the ONE module-colour registry. A real module paints from its own
 * step on the module identity ramp (`pb-module-solve|vsm|hoshin|lama`,
 * ADR-0092), and that colour lives in chrome only — the tab underline
 * (`tabAccent`), the launcher tile (`tile` + `tint`), the launcher glyph
 * (`accentTextClass`) and the settings dot (`dotClass`) — never inside a
 * status pill. Knowledge Studio is neutral (a utility, not a module) and DPM
 * has no colour until it ships, so neither carries a tab accent or a tile.
 * Every class is a literal so Tailwind can see it.
 */
interface ModulePresentation {
  readonly short: string;
  readonly blurb: string;
  /**
   * The module's MARK — the one glyph it wears in the tab strip, the launcher
   * tile, the module home and the Settings roster.
   *
   * It lives here, beside the module's colour, because it is the same kind of
   * fact and it drifted the moment it did not: `ALL_MODULES` gave SOLVE a
   * `Workflow` glyph while `SETTINGS_MODULES` gave it a `ClipboardList`, so one
   * module wore two different marks on two screens the same user visits. Both
   * registries now read this field and there is nowhere for them to disagree.
   *
   * Four of the six are drawn by us (`@playbookai/ui` icons) because the lean
   * symbols do not exist in lucide — see `packages/ui/src/icons/lean.ts`.
   */
  readonly icon: LucideIcon;
  /** The module's `-text` channel, for the launcher icon glyph. */
  readonly accentTextClass: string;
  /** The same identity as a small filled dot (Settings › Account › Your
   * modules). */
  readonly dotClass: string;
  /** The TabStrip underline. Absent for a neutral module. */
  readonly tabAccent?: TabAccent;
  /** The launcher icon tile's fill + border. Absent for a neutral module. */
  readonly tile?: string;
  /** The `[--pb-hover-tint:…]` channel the launcher tile's `hover-tint`
   * reads. Absent for a neutral module. */
  readonly tint?: string;
  /**
   * The semantic onboarding-tour target this module's launcher card exposes, if
   * any. Declared HERE rather than in `@playbookai/ui` for the same reason the
   * accent is: the design system never names a module.
   *
   * Only Knowledge Studio carries one today, because it is the one module an
   * approved tour step points at from another module's workspace — and the
   * launcher card IS its entry point (it has no top-bar or Home control). Every
   * other module's tour anchors live inside that module's own screens.
   */
  readonly tourId?: string;
}

const MODULE_PRESENTATION: Record<ModuleId, ModulePresentation> = {
  solve: {
    short: "SOLVE",
    /* an A3 is the sheet five-phase problem solving is walked through. */
    icon: A3Sheet,
    blurb: "Structured five-phase problem solving with a collaborative canvas.",
    accentTextClass: "text-pb-module-solve-text",
    dotClass: "bg-pb-module-solve",
    tabAccent: "pb-module-solve",
    tile: "bg-pb-module-solve-soft border-pb-module-solve/30",
    tint: "[--pb-hover-tint:var(--pb-module-solve)]",
  },
  vsm: {
    short: "VSM",
    /* process boxes over the sawtooth lead-time ladder. */
    icon: ProcessChain,
    blurb: "Map value streams; visualise flow, lead time, and bottlenecks.",
    accentTextClass: "text-pb-module-vsm-text",
    dotClass: "bg-pb-module-vsm",
    tabAccent: "pb-module-vsm",
    tile: "bg-pb-module-vsm-soft border-pb-module-vsm/30",
    tint: "[--pb-hover-tint:var(--pb-module-vsm)]",
  },
  hoshin: {
    short: "HOSHIN",
    /* 方針 hoshin = 方 direction + 針 needle: the name IS a compass needle. */
    icon: Compass,
    blurb: "Strategy deployment via X-Matrix and bowler chart.",
    accentTextClass: "text-pb-module-hoshin-text",
    dotClass: "bg-pb-module-hoshin",
    tabAccent: "pb-module-hoshin",
    tile: "bg-pb-module-hoshin-soft border-pb-module-hoshin/30",
    tint: "[--pb-hover-tint:var(--pb-module-hoshin)]",
  },
  dpm: {
    short: "DPM",
    /* the board a daily tier huddle forms around. */
    icon: TierBoard,
    blurb:
      "Daily Performance Management — tier meetings, KPI review, escalations.",
    accentTextClass: "text-pb-text-secondary",
    dotClass: "bg-pb-text-muted",
  },
  lama: {
    short: "LAMA",
    /* the five-axis spider plot an assessment is reported on. */
    icon: MaturityWeb,
    blurb:
      "Complete assigned assessments; leads manage questionnaires, campaigns, and reports.",
    accentTextClass: "text-pb-module-lama-text",
    dotClass: "bg-pb-module-lama",
    tabAccent: "pb-module-lama",
    tile: "bg-pb-module-lama-soft border-pb-module-lama/30",
    tint: "[--pb-hover-tint:var(--pb-module-lama)]",
  },
  knowledge_studio: {
    short: "STUDIO",
    /* a reference shelf. */
    icon: BookOpen,
    blurb: `Read ${PRODUCT_NAME} frameworks, workshop guidance, and shared references.`,
    /* Bronze. ADR-0092 shipped Studio NEUTRAL, reasoning that a reference
       shelf is a utility rather than a product module; that left one entry in
       the launcher with no identity of its own, so the call is reversed for
       MVP. Bronze because the gate leaves only two openings and the other is
       the magenta family already rejected — and sepia suits a library. */
    accentTextClass: "text-pb-module-studio-text",
    dotClass: "bg-pb-module-studio",
    tabAccent: "pb-module-studio",
    tile: "bg-pb-module-studio-soft border-pb-module-studio/30",
    tint: "[--pb-hover-tint:var(--pb-module-studio)]",
    tourId: "shell.knowledge-studio",
  },
};

/** The module's MARK — the one glyph it wears everywhere. Both module
 * registries read this, so they cannot drift apart again. */
export function moduleIcon(id: ModuleId): LucideIcon {
  return MODULE_PRESENTATION[id].icon;
}

/** The module's identity as a text class, for icons and glyphs (one source:
 * the launcher presentation, which reads the module identity ramp). */
export function moduleAccentTextClass(id: ModuleId): string {
  return MODULE_PRESENTATION[id].accentTextClass;
}

/** The module identities as small filled dots (Settings › Account › Your
 * modules), derived from the ONE presentation table above so the two can never
 * drift; the dot is the module's ramp fill, or the muted neutral for a module
 * that has no colour. */
export const MODULE_DOT_CLASS: Record<ModuleId, string> = Object.fromEntries(
  (Object.keys(MODULE_PRESENTATION) as ModuleId[]).map((id) => [
    id,
    MODULE_PRESENTATION[id].dotClass,
  ]),
) as Record<ModuleId, string>;

/* The module ramp as StatusDot tints; a module missing here has no colour. */
const MODULE_DOT_TINT: Partial<Record<ModuleId, ModuleTint>> = {
  solve: "solve",
  vsm: "vsm",
  hoshin: "hoshin",
  lama: "lama",
  knowledge_studio: "studio",
};

/** The same identity as `StatusDot` props, painting the same fill as
 * `MODULE_DOT_CLASS`: the module's tint, or the neutral tone (the muted fill)
 * for a module that has no colour. Undefined for an id that is not a module,
 * such as a platform notification's. */
export function moduleDotProps(
  id: string,
): { tint: ModuleTint } | { tone: "neutral" } | undefined {
  if (!Object.prototype.hasOwnProperty.call(MODULE_PRESENTATION, id)) {
    return undefined;
  }
  const tint = MODULE_DOT_TINT[id as ModuleId];
  return tint ? { tint } : { tone: "neutral" };
}

/**
 * Apps hidden from discovery surfaces (the launcher grid and ⌘K search) when
 * the "Show more apps" launcher preference is OFF. Ported from the pbdesign
 * oracle (`data/apps.ts`). They stay mountable — a direct deep link or the
 * AppLayout bootstrap still resolves them — so only *discovery* is gated.
 *
 * `transformation-map` isn't a mountable module today (it has no `ModuleManifest`)
 * so it can't appear in our discovery surfaces, but it's listed for parity with
 * the oracle and the settings copy ("e.g. DPM, Transformation Map").
 */
export const HIDDEN_WHEN_COMPACT: ReadonlySet<string> = new Set([
  "dpm",
  "transformation-map",
]);

/**
 * Filter module manifests for a discovery surface. When `showMoreApps` is false
 * the compact-hidden modules are dropped; otherwise the list passes through
 * unchanged. Order is preserved.
 */
export function filterModulesForVisibility(
  manifests: readonly ModuleManifest[],
  showMoreApps: boolean,
): readonly ModuleManifest[] {
  if (showMoreApps) return manifests;
  return manifests.filter((m) => !HIDDEN_WHEN_COMPACT.has(m.id));
}

/**
 * Favourites first, in the order the person saved them, then the rest in
 * manifest order (PB-5134, t11). Ids the roster does not know are ignored:
 * a favourite for a module this deployment lacks never blanks the grid.
 */
export function orderFavouritesFirst(
  manifests: readonly ModuleManifest[],
  favourites: readonly string[],
): readonly ModuleManifest[] {
  const byId = new Map(manifests.map((m) => [m.id as string, m]));
  // A `Set` so a duplicated id (the store validates entries only as strings)
  // pins one entry, in first-saved order.
  const pinned = [...new Set(favourites)]
    .map((id) => byId.get(id))
    .filter((m): m is ModuleManifest => m !== undefined);
  const pinnedIds = new Set(pinned.map((m) => m.id as string));
  return [...pinned, ...manifests.filter((m) => !pinnedIds.has(m.id))];
}

/**
 * Project a list of enabled `ModuleManifest`s into the shape the AppLauncher
 * expects. Single source of truth for icon, short tag, blurb, accent, tile and
 * tint — the launcher and the tab strip both read from here so they never
 * drift. A neutral module carries no `tile` or `tint` key at all.
 */
export function manifestsToLauncherEntries(
  manifests: readonly ModuleManifest[],
): readonly AppLauncherEntry[] {
  return manifests.map((m) => {
    const p = MODULE_PRESENTATION[m.id];
    return {
      id: m.id,
      name: m.label,
      short: p.short,
      blurb: p.blurb,
      icon: m.icon,
      accent: p.accentTextClass,
      ...(p.tile ? { tile: p.tile } : {}),
      ...(p.tint ? { tint: p.tint } : {}),
      ...(p.tourId ? { tourId: p.tourId } : {}),
    };
  });
}

/**
 * How the shell recognises a module's HOME (list) tab, as opposed to one of
 * its entity tabs. Declared per module rather than inferred, because no rule
 * derived from the route alone holds for every module — see below.
 *
 * - `instanceSegment` — the home route is `<routeBase>/<instanceId>` and the
 *   module's entity routes are DEEPER, so one segment means home.
 * - `fixedSegment` — the module is a singleton and always lands on the same
 *   segment, whatever instance id the caller minted.
 * - `baseRoute` — the module's home IS `routeBase` with no segment at all, so
 *   its entity routes (one segment) can never be confused with it. The shell
 *   does not mint an instance id for these modules.
 */
type HomeRoutePolicy =
  | { readonly kind: "instanceSegment" }
  | { readonly kind: "fixedSegment"; readonly segment: string }
  | { readonly kind: "baseRoute" };

/**
 * One entry per module — a `Record<ModuleId, …>`, like `MODULE_PRESENTATION`
 * above, so adding a module id is a COMPILE ERROR until its policy is stated.
 * That forcing function is the point: a silent default would let a new module
 * be misclassified with nothing to catch it.
 *
 * `hoshin` is `baseRoute`. Its router's INDEX route is the plan grid and its
 * `:instanceId` segment is a plan id — so the shell minting an instance for it
 * produced `/app/hoshin/<n>`, a URL shaped exactly like a plan and meaning the
 * grid. (`HoshinWorkspace` carries a `/^\d+$/` guard purely to survive that.)
 * Anchoring Hoshin's home at `routeBase` removes the collision at its source
 * instead of asking the shell to guess: home has zero segments, a plan has one.
 */
const MODULE_HOME_ROUTE: Record<ModuleId, HomeRoutePolicy> = {
  solve: { kind: "instanceSegment" },
  vsm: { kind: "instanceSegment" },
  hoshin: { kind: "baseRoute" },
  dpm: { kind: "instanceSegment" },
  lama: { kind: "instanceSegment" },
  knowledge_studio: {
    kind: "fixedSegment",
    segment: DEFAULT_KNOWLEDGE_INSTANCE_ID,
  },
};

/**
 * The instance id this module pins for every tab, or undefined when the module
 * takes whatever the caller minted.
 *
 * Read from {@link MODULE_HOME_ROUTE} so the segment a module MINTS and the
 * segment it RECOGNISES come from one declaration and cannot drift apart.
 */
function pinnedInstanceId(m: ModuleManifest): string | undefined {
  const policy = MODULE_HOME_ROUTE[m.id];
  return policy.kind === "fixedSegment" ? policy.segment : undefined;
}

/**
 * Does this module consume a minted instance id at all? Only `instanceSegment`
 * modules do. A `fixedSegment` module lands on its pinned segment and a
 * `baseRoute` module on `routeBase`, so minting for either bumps the per-module
 * counter for an id that is then discarded — state mutated for nothing, and
 * inconsistent with the bootstrap, which does not mint for them.
 */
export function usesInstanceSegment(m: ModuleManifest): boolean {
  return MODULE_HOME_ROUTE[m.id].kind === "instanceSegment";
}

/**
 * Does this module's home live at `routeBase` itself, with no instance segment?
 * The deep-link bootstrap consults this so it does not mint an id these modules
 * would only have to work around.
 */
export function isHomeAtRouteBase(m: ModuleManifest): boolean {
  return MODULE_HOME_ROUTE[m.id].kind === "baseRoute";
}

/**
 * Project a manifest + opaque instance id into a `Tab`. Each launcher pick or
 * deep-link bootstrap mints its own instance id, so two SOLVE tabs coexist as
 * `solve-<a>` at `/app/solve/<a>` and `solve-<b>` at `/app/solve/<b>`. The
 * placeholder modules ignore the segment; real modules treat it as their
 * workspace id. A `fixedSegment` module ignores the minted id entirely.
 *
 * No `module` is set: the prototype labels tabs with just the entity title (or
 * the module name for the list), never a `<module> / <label>` prefix. The icon
 * + accent already convey which module a tab belongs to. A neutral module has
 * no `accent` key at all, rather than an explicit `undefined`.
 */
export function manifestToTab(m: ModuleManifest, instanceId: string): Tab {
  const { tabAccent } = MODULE_PRESENTATION[m.id];
  const accent = tabAccent ? { accent: tabAccent } : {};
  if (isHomeAtRouteBase(m)) {
    return {
      id: `${m.id}-home`,
      label: m.label,
      route: m.routeBase,
      icon: m.icon,
      ...accent,
    };
  }
  const resolvedInstanceId = pinnedInstanceId(m) ?? instanceId;
  return {
    id: `${m.id}-${resolvedInstanceId}`,
    label: m.label,
    route: `${m.routeBase}/${resolvedInstanceId}`,
    icon: m.icon,
    ...accent,
  };
}

/**
 * Is this tab the module's home tab? Answered from the module's declared
 * {@link MODULE_HOME_ROUTE} policy — never from the tab id, and never from
 * segment count alone.
 *
 * The id cannot answer it: `openModuleEntityTab` keys entity tabs
 * `<moduleId>-<entityId>`, the same shape a home tab gets from
 * `<moduleId>-<instanceId>`.
 *
 * Segment count cannot answer it on its own either, which is the subtle part —
 * it holds for SOLVE and VSM but not for Hoshin. That is exactly what the
 * policy declares, per module, instead of the shell guessing.
 */
export function isModuleHomeTab(tab: Tab, m: ModuleManifest): boolean {
  const policy = MODULE_HOME_ROUTE[m.id];
  if (policy.kind === "baseRoute") return tab.route === m.routeBase;

  const prefix = `${m.routeBase}/`;
  if (!tab.route.startsWith(prefix)) return false;
  const rest = tab.route.slice(prefix.length);
  if (rest.length === 0 || /[/?#]/.test(rest)) return false;
  if (m.id === "lama" && rest === LAMA_ASSESS_SEGMENT) return false;
  if (isItemIdTabSlot(m, rest)) return false;

  return policy.kind === "fixedSegment" ? rest === policy.segment : true;
}

/**
 * The modules whose tab slot (`<routeBase>/<slot>`) only ever holds a tab
 * number from the counter (`store/tabs.ts` `nextCount`). An item id there is
 * therefore an item link, never a tab (d1).
 */
const ITEM_LINK_MODULES: ReadonlySet<ModuleId> = new Set(["solve", "vsm"]);

/** Is `segment`, in this module's tab slot, an item id rather than a tab number? */
export function isItemIdTabSlot(
  m: Pick<ModuleManifest, "id">,
  segment: string,
): boolean {
  return ITEM_LINK_MODULES.has(m.id) && UUID_PATTERN.test(segment);
}

/** A SOLVE or VSM address that names an item by id, with no tab number. */
export interface ItemLink {
  readonly module: ModuleManifest;
  readonly itemId: string;
  /** The path after the item id, such as `/edit`; empty when there is none. */
  readonly tail: string;
}

/**
 * Read an item link (d1): `/app/solve/<solveId>`, `/app/vsm/<vsmId>`, the
 * bell's old `/app/vsm/<vsmId>/<vsmId>`, or any of them with a deeper path.
 * Only an ENABLED module's link is one, so a switched-off module's link still
 * reaches its not-available page. The item is the next segment when that is an
 * id too, else the id in the tab slot.
 */
export function itemLinkFor(
  path: string,
  enabledModules: readonly ModuleManifest[],
): ItemLink | undefined {
  const module = enabledModules.find((m) => path.startsWith(`${m.routeBase}/`));
  if (!module) return undefined;
  const segments = path.slice(module.routeBase.length + 1).split("/");
  const [slot = "", next = ""] = segments;
  if (!isItemIdTabSlot(module, slot)) return undefined;
  const itemAt = UUID_PATTERN.test(next) ? 1 : 0;
  const rest = segments.slice(itemAt + 1);
  return {
    module,
    itemId: itemAt === 1 ? next : slot,
    tail: rest.length > 0 ? `/${rest.join("/")}` : "",
  };
}
